import React from "react";
import { FaHome, FaMoneyBillWave, FaPhone, FaClock } from "react-icons/fa";
import { motion } from "framer-motion";

interface PendingPayment {
    tenantName: string;
    propertyName: string;
    totalBalanceAmount: number;
    dueDate: string | number;
    tenantContact: string;
}

interface CurrentPayment {
    tenantName: string;
    propertyName: string;
    rentAmount: number;
    dueDate: string | number;
    tenantContact: string;
    formattedDueDate?: string;
}

interface UpcomingPayment {
    tenantName: string;
    propertyName: string;
    rentAmount: number;
    dueDate: string | number;
    tenantContact: string;
    formattedDueDate?: string;
}

interface OutstandingAmountListProps {
    current: CurrentPayment[];
    pending: PendingPayment[];
    upcoming: UpcomingPayment[];
}

const SectionTitle = ({ title }: { title: string }) => (
    <h3 className="text-base sm:text-lg font-semibold mb-2 mt-4 text-black">{title}</h3>
);

const OutstandingAmountList: React.FC<OutstandingAmountListProps> = ({ 
    current, 
    pending, 
    upcoming 
}) => {
    const profileColors = [
        "bg-red-500", "bg-blue-500", "bg-green-500",
        "bg-yellow-500", "bg-purple-500", "bg-pink-500"
    ];

    const renderCard = (
        item: PendingPayment | UpcomingPayment | CurrentPayment, 
        index: number, 
        type: "pending" | "upcoming" | "current"
    ) => (
        <motion.li
            key={index}
            className="mb-4 p-3 sm:p-4 border border-gray-200 rounded-lg bg-white flex flex-col items-start transition-all duration-300 hover:bg-gray-50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            whileHover={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" }}
        >
            <div className="flex items-center w-full mb-3">
                <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full text-white flex items-center justify-center mr-3 font-bold text-lg shadow-md ${
                        profileColors[index % profileColors.length]
                    }`}
                >
                    {item.tenantName.charAt(0).toUpperCase()}
                </div>
                
                <div className="text-black font-semibold text-sm sm:text-lg">
                    {item.tenantName}
                </div>
            </div>

            <div className="w-full">
                <div className="grid grid-cols-1 gap-2 sm:gap-3">
                    <div className="text-black flex items-center text-xs sm:text-base">
                        <FaHome className="mr-2 text-gray-500 min-w-[16px]" />
                        <span className="truncate">Property Name: {item.propertyName}</span>
                    </div>
                    
                    <div className="text-black flex items-center text-xs sm:text-base">
                        <FaPhone className="mr-2 text-blue-500 min-w-[16px]" />
                        <span className="truncate">Tenant Phone Number: {item.tenantContact}</span>
                    </div>
                    
                    <div className="text-black flex items-center text-xs sm:text-base">
                        <FaClock className="mr-2 text-orange-500 min-w-[16px]" />
                        <span>Due Date: {type === "upcoming" && "formattedDueDate" in item ? 
                            item.formattedDueDate : item.dueDate}</span>
                    </div>
                    
                    {type === "pending" && "totalBalanceAmount" in item && (
                        <div className="text-black flex items-center text-xs sm:text-base">
                            <FaMoneyBillWave className="mr-2 text-green-500 min-w-[16px]" />
                            <span>Total Pending Amount: ₹{item.totalBalanceAmount.toFixed(2)}</span>
                        </div>
                    )}
                    
                    {(type === "upcoming" || type === "current") && "rentAmount" in item && (
                        <div className="text-black flex items-center text-xs sm:text-base">
                            <FaMoneyBillWave className="mr-2 text-green-500 min-w-[16px]" />
                            <span>
                                {type === "upcoming" ? "Upcoming" : "Current"} Rent Amount: 
                                ₹{item.rentAmount.toFixed(2)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </motion.li>
    );

    return (
        <div className="h-[400px] overflow-y-auto rounded-lg p-2 sm:p-4 bg-gray-50">
            <ul className="list-none p-0 space-y-3">
                {pending.length > 0 && (
                    <>
                        <SectionTitle title="🔴 Pending Payments" />
                        {pending.map((item, idx) => renderCard(item, idx, "pending"))}
                    </>
                )}

                {current.length > 0 && (
                    <>
                        <SectionTitle title="🟢 Current Payments" />
                        {current.map((item, idx) => renderCard(item, idx, "current"))}
                    </>
                )}

                {upcoming.length > 0 && (
                    <>
                        <SectionTitle title="🟡 Upcoming Payments" />
                        {upcoming.map((item, idx) => renderCard(item, idx, "upcoming"))}
                    </>
                )}

                {current.length === 0 && pending.length === 0 && upcoming.length === 0 && (
                    <p className="text-center text-gray-500 py-4 text-sm sm:text-base">
                        No outstanding payments found.
                    </p>
                )}
            </ul>
        </div>
    );
};

export default OutstandingAmountList;
export type { PendingPayment, UpcomingPayment, CurrentPayment, OutstandingAmountListProps };


// import React from "react";
// import { FaHome, FaMoneyBillWave, FaPhone, FaClock } from "react-icons/fa";
// import { motion } from "framer-motion";

// interface PendingPayment {
//     tenantName: string;
//     propertyName: string;
//     totalBalanceAmount: number;
//     dueDate: string | number;
//     tenantContact: string;
// }

// interface CurrentPayment {
//     tenantName: string;
//     propertyName: string;
//     rentAmount: number;
//     dueDate: string | number;
//     tenantContact: string;
//     formattedDueDate?: string;
// }

// interface UpcomingPayment {
//     tenantName: string;
//     propertyName: string;
//     rentAmount: number;
//     dueDate: string | number;
//     tenantContact: string;
//     formattedDueDate?: string;
// }

// interface OutstandingAmountListProps {
//     current: CurrentPayment[];
//     pending: PendingPayment[];
//     upcoming: UpcomingPayment[];
// }

// const SectionTitle = ({ title }: { title: string }) => (
//     <h3 className="text-base sm:text-lg font-semibold mb-2 mt-4 text-black">{title}</h3>
// );

// const OutstandingAmountList: React.FC<OutstandingAmountListProps> = ({ 
//     current, 
//     pending, 
//     upcoming 
// }) => {
//     const profileColors = [
//         "bg-red-500", "bg-blue-500", "bg-green-500",
//         "bg-yellow-500", "bg-purple-500", "bg-pink-500"
//     ];

//     const renderCard = (
//         item: PendingPayment | UpcomingPayment | CurrentPayment, 
//         index: number, 
//         type: "pending" | "upcoming" | "current"
//     ) => (
//         <motion.li
//             key={index}
//             className="mb-4 p-3 border border-gray-200 rounded-lg bg-white"
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             exit={{ opacity: 0, y: 20 }}
//             transition={{ duration: 0.2 }}
//         >
//             <div className="flex items-center mb-2">
//                 <div
//                     className={`w-10 h-10 rounded-full text-white flex items-center justify-center mr-3 font-bold text-lg shadow-md ${
//                         profileColors[index % profileColors.length]
//                     }`}
//                 >
//                     {item.tenantName.charAt(0).toUpperCase()}
//                 </div>
                
//                 <strong className="text-black font-semibold text-sm sm:text-base">
//                     {item.tenantName}
//                 </strong>
//             </div>

//             <div className="ml-13 pl-1">
//                 <div className="text-black text-xs sm:text-sm mb-1">
//                     <span>Tenant Phone Number : {item.tenantContact}</span>
//                 </div>
                
//                 <div className="text-black text-xs sm:text-sm mb-1">
//                     <span>Property Name : {item.propertyName}</span>
//                 </div>
                
//                 <div className="text-black text-xs sm:text-sm mb-1">
//                     <span>Due Date : {type === "upcoming" && "formattedDueDate" in item ? 
//                         item.formattedDueDate : item.dueDate}</span>
//                 </div>
                
//                 {type === "pending" && "totalBalanceAmount" in item && (
//                     <div className="text-black text-xs sm:text-sm">
//                         <span>Total Pending Amount : ₹{item.totalBalanceAmount.toFixed(2)}</span>
//                     </div>
//                 )}
                
//                 {(type === "upcoming" || type === "current") && "rentAmount" in item && (
//                     <div className="text-black text-xs sm:text-sm">
//                         <span>
//                             {type === "upcoming" ? "Upcoming" : "Current"} Rent Amount : 
//                             ₹{item.rentAmount.toFixed(2)}
//                         </span>
//                     </div>
//                 )}
//             </div>
//         </motion.li>
//     );

//     return (
//         <div className="h-[400px] overflow-y-auto rounded-lg p-2 sm:p-4 bg-gray-50">
//             <ul className="list-none p-0 space-y-3">
//                 {pending.length > 0 && (
//                     <>
//                         <SectionTitle title="🔴 Pending Payments" />
//                         {pending.map((item, idx) => renderCard(item, idx, "pending"))}
//                     </>
//                 )}

//                 {current.length > 0 && (
//                     <>
//                         <SectionTitle title="🟢 Current Payments" />
//                         {current.map((item, idx) => renderCard(item, idx, "current"))}
//                     </>
//                 )}

//                 {upcoming.length > 0 && (
//                     <>
//                         <SectionTitle title="🟡 Upcoming Payments" />
//                         {upcoming.map((item, idx) => renderCard(item, idx, "upcoming"))}
//                     </>
//                 )}

//                 {current.length === 0 && pending.length === 0 && upcoming.length === 0 && (
//                     <p className="text-center text-gray-500 py-4 text-sm sm:text-base">
//                         No outstanding payments found.
//                     </p>
//                 )}
//             </ul>
//         </div>
//     );
// };

// export default OutstandingAmountList;
// export type { PendingPayment, UpcomingPayment, CurrentPayment, OutstandingAmountListProps };